import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, FileQuestion, Lightbulb, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exercises database by module
const EXERCISES = {
  mod_2h_1: { fr: { type: 'prompt', question: 'Écrivez un prompt pour demander à ChatGPT de vous expliquer ce qu\'est l\'IA comme si vous aviez 10 ans.', hint: 'Utilisez la méthode CREA : Contexte + Rôle + Exemple + Action' }, en: { type: 'prompt', question: 'Write a prompt to ask ChatGPT to explain what AI is as if you were 10 years old.', hint: 'Use the CREA method: Context + Role + Example + Action' }},
  mod_2h_2: { fr: { type: 'match', question: 'Associez chaque outil à son usage principal', pairs: [['ChatGPT', 'Conversation et texte'], ['DALL-E', 'Génération d\'images'], ['GitHub Copilot', 'Assistance au code'], ['Perplexity', 'Recherche avec sources']] }, en: { type: 'match', question: 'Match each tool with its primary use', pairs: [['ChatGPT', 'Conversation and text'], ['DALL-E', 'Image generation'], ['GitHub Copilot', 'Code assistance'], ['Perplexity', 'Search with sources']] }},
  mod_4h_1: { fr: { type: 'prompt', question: 'Donnez un exemple concret d\'apprentissage supervisé dans la vie quotidienne (ex: détection de spam).', hint: 'Pensez à un système qui apprend à partir d\'exemples étiquetés' }, en: { type: 'prompt', question: 'Give a concrete example of supervised learning in daily life (e.g.: spam detection).', hint: 'Think of a system that learns from labeled examples' }},
  mod_4h_3: { fr: { type: 'prompt', question: 'Décrivez un workflow de 3 étapes pour automatiser la rédaction d\'un rapport hebdomadaire avec l\'IA.', hint: 'Collecte de données -> Prompt IA -> Mise en forme' }, en: { type: 'prompt', question: 'Describe a 3-step workflow to automate writing a weekly report with AI.', hint: 'Data collection -> AI Prompt -> Formatting' }},
  mod_8h_4: { fr: { type: 'prompt', question: 'Rédigez un system prompt pour un assistant IA spécialisé dans le service client d\'un e-commerce.', hint: 'Définissez : le rôle, le ton, les limites, et les actions possibles' }, en: { type: 'prompt', question: 'Write a system prompt for an AI assistant specialized in e-commerce customer service.', hint: 'Define: the role, tone, limits, and possible actions' }},
  mod_8h_5: { fr: { type: 'prompt', question: 'Identifiez 3 cas d\'usage IA pour une entreprise de restauration. Pour chaque cas, estimez l\'impact (faible/moyen/fort).', hint: 'Pensez aux opérations, au service client, et au marketing' }, en: { type: 'prompt', question: 'Identify 3 AI use cases for a restaurant business. For each, estimate the impact (low/medium/high).', hint: 'Think about operations, customer service, and marketing' }},
  mod_8h_6: { fr: { type: 'prompt', question: 'Préparez un pitch de 30 secondes pour convaincre votre directeur d\'investir dans un chatbot IA pour le service client.', hint: 'Problème actuel -> Solution IA -> ROI estimé -> Quick win' }, en: { type: 'prompt', question: 'Prepare a 30-second pitch to convince your director to invest in an AI chatbot for customer service.', hint: 'Current problem -> AI Solution -> Estimated ROI -> Quick win' }},
  mod_8h_7: { fr: { type: 'prompt', question: 'Définissez les critères de succès (KPIs) pour un projet IA de détection de fraude bancaire.', hint: 'Précision, rappel, faux positifs, temps de détection, économie réalisée' }, en: { type: 'prompt', question: 'Define success criteria (KPIs) for an AI fraud detection project in banking.', hint: 'Precision, recall, false positives, detection time, cost savings' }},
};

// Simple markdown renderer
const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-xl font-semibold text-slate-900 mt-6 mb-3" style={{ fontFamily: 'Poppins' }}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-lg font-semibold text-slate-800 mt-4 mb-2" style={{ fontFamily: 'Poppins' }}>{line.slice(4)}</h3>);
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="border-l-3 border-blue-500 bg-slate-50 px-4 py-3 my-3 rounded-r-lg text-slate-600 italic">
          {line.slice(2)}
        </blockquote>
      );
    } else if (line.startsWith('- **')) {
      const match = line.match(/- \*\*(.+?)\*\*\s*:?\s*(.*)/);
      if (match) {
        elements.push(
          <li key={i} className="mb-2 text-slate-600 ml-4 list-disc">
            <strong className="text-slate-900">{match[1]}</strong>{match[2] ? ` : ${match[2]}` : ''}
          </li>
        );
      } else {
        elements.push(<li key={i} className="mb-2 text-slate-600 ml-4 list-disc">{line.slice(2)}</li>);
      }
    } else if (line.startsWith('- ')) {
      elements.push(<li key={i} className="mb-2 text-slate-600 ml-4 list-disc">{processInlineFormatting(line.slice(2))}</li>);
    } else if (/^\d+\.\s/.test(line)) {
      elements.push(<li key={i} className="mb-2 text-slate-600 ml-4 list-decimal">{processInlineFormatting(line.replace(/^\d+\.\s/, ''))}</li>);
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-slate-600 leading-relaxed mb-2">{processInlineFormatting(line)}</p>);
    }
    i++;
  }

  return <div className="prose max-w-none">{elements}</div>;
};

const processInlineFormatting = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-slate-900 font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export default function CoursePage() {
  const { moduleId } = useParams();
  const { language, t } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasQuiz, setHasQuiz] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const modRes = await axios.get(`${API}/modules/${moduleId}`);
        setModule(modRes.data);

        // Fetch all modules for this program
        const modsRes = await axios.get(`${API}/programs/${modRes.data.program_id}/modules`);
        setAllModules(modsRes.data);

        // Check quiz availability
        try {
          await axios.get(`${API}/quizzes/${moduleId}`);
          setHasQuiz(true);
        } catch { setHasQuiz(false); }

        // Check user progress
        if (user) {
          const progressRes = await axios.get(`${API}/progress`, { headers: getAuthHeaders(), withCredentials: true });
          const found = progressRes.data.progress?.find(p => p.module_id === moduleId);
          if (found?.completed) setIsCompleted(true);
        }
      } catch (err) {
        console.error('Course fetch error:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [moduleId, user, getAuthHeaders]);

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/progress`, { module_id: moduleId, completed: true }, { headers: getAuthHeaders(), withCredentials: true });
      setIsCompleted(true);
    } catch (err) {
      console.error('Complete error:', err);
    }
  };

  const currentIndex = allModules.findIndex(m => m.module_id === moduleId);
  const prevModule = currentIndex > 0 ? allModules[currentIndex - 1] : null;
  const nextModule = currentIndex < allModules.length - 1 ? allModules[currentIndex + 1] : null;

  if (loading || !module) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const content = language === 'fr' ? module.content_fr : module.content_en;

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="course-page">
      <SEO
        title={module ? (language === 'fr' ? module.title_fr : module.title_en) : 'Module'}
        description={module ? (language === 'fr' ? module.description_fr : module.description_en) : ''}
        path={`/course/${moduleId}`}
        noIndex
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Button variant="ghost" className="mb-4 text-slate-600" onClick={() => navigate(`/programs/${module.program_id}`)} data-testid="back-to-program">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-8 h-8 rounded-lg" data-testid="course-logo" />
            <Badge variant="outline" className="text-xs">
              <Clock className="w-3 h-3 mr-1" /> {module.duration_minutes} min
            </Badge>
            {isCompleted && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle className="w-3 h-3 mr-1" /> {t('course_completed')}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="course-title">
            {language === 'fr' ? module.title_fr : module.title_en}
          </h1>
          <p className="text-slate-500 mt-2">{language === 'fr' ? module.description_fr : module.description_en}</p>
        </div>

        {/* Video Section */}
        {(module.video_url || module.video_url_fr || module.video_url_en) && (
          <div className="bg-slate-900 rounded-xl overflow-hidden mb-6 shadow-lg" data-testid="course-video">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={language === 'fr' ? (module.video_url_fr || module.video_url) : (module.video_url_en || module.video_url)}
                title={language === 'fr' ? module.title_fr : module.title_en}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 mb-8" data-testid="course-content">
          {renderMarkdown(content)}
        </div>

        {/* Interactive Exercise */}
        {EXERCISES[moduleId] && <ExerciseBlock exercise={EXERCISES[moduleId]} language={language} moduleId={moduleId} />}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {!isCompleted && user && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
              onClick={handleComplete}
              data-testid="mark-complete-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> {t('course_complete')}
            </Button>
          )}
          {hasQuiz && (
            <Button
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => navigate(`/quiz/${moduleId}`)}
              data-testid="take-quiz-btn"
            >
              <FileQuestion className="w-4 h-4 mr-2" /> {t('course_quiz')}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between border-t border-slate-200 pt-6">
          {prevModule ? (
            <Button variant="ghost" className="text-slate-600" onClick={() => navigate(`/course/${prevModule.module_id}`)} data-testid="prev-module-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('course_prev')}
            </Button>
          ) : <div />}
          {nextModule ? (
            <Button variant="ghost" className="text-blue-600" onClick={() => navigate(`/course/${nextModule.module_id}`)} data-testid="next-module-btn">
              {t('course_next')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : <div />}
        </div>
      </div>
    </div>
  );
}

function ExerciseBlock({ exercise, language, moduleId }) {
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const ex = language === 'fr' ? exercise.fr : exercise.en;

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6 mb-8" data-testid={`exercise-${moduleId}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
          <Lightbulb className="w-4 h-4 text-orange-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>
          {language === 'fr' ? 'Exercice pratique' : 'Practical Exercise'}
        </h3>
      </div>

      <p className="text-sm text-slate-700 font-medium mb-4">{ex.question}</p>

      {ex.type === 'prompt' && (
        <>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-orange-200 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none resize-none bg-white"
            rows={4}
            placeholder={language === 'fr' ? 'Écrivez votre réponse ici...' : 'Write your answer here...'}
            disabled={submitted}
            data-testid={`exercise-input-${moduleId}`}
          />
          {!submitted ? (
            <div className="flex items-center gap-3 mt-3">
              <Button
                size="sm"
                className="bg-orange-500 text-white hover:bg-orange-600"
                disabled={!answer.trim()}
                onClick={() => setSubmitted(true)}
                data-testid={`exercise-submit-${moduleId}`}
              >
                <Check className="w-3.5 h-3.5 mr-1" /> {language === 'fr' ? 'Valider' : 'Submit'}
              </Button>
              <button
                onClick={() => setShowHint(!showHint)}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                data-testid={`exercise-hint-toggle-${moduleId}`}
              >
                {showHint ? (language === 'fr' ? 'Masquer l\'indice' : 'Hide hint') : (language === 'fr' ? 'Voir l\'indice' : 'Show hint')}
              </button>
            </div>
          ) : (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2" data-testid={`exercise-success-${moduleId}`}>
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-sm text-emerald-700">
                {language === 'fr' ? 'Bravo ! Exercice complété. Comparez votre réponse avec le contenu du cours.' : 'Well done! Exercise completed. Compare your answer with the course content.'}
              </p>
            </div>
          )}
          {showHint && !submitted && (
            <p className="mt-2 text-xs text-orange-600 bg-orange-100 rounded-lg p-2 border border-orange-200">
              {language === 'fr' ? 'Indice' : 'Hint'}: {ex.hint}
            </p>
          )}
        </>
      )}

      {ex.type === 'match' && <MatchExercise pairs={ex.pairs} language={language} moduleId={moduleId} />}
    </div>
  );
}

function MatchExercise({ pairs, language, moduleId }) {
  const [selected, setSelected] = useState({});
  const [checked, setChecked] = useState(false);
  const tools = pairs.map(p => p[0]);
  const usages = pairs.map(p => p[1]).sort(() => Math.random() - 0.5);

  const handleCheck = () => setChecked(true);
  const correct = Object.entries(selected).filter(([tool, usage]) => pairs.find(p => p[0] === tool && p[1] === usage)).length;

  return (
    <div data-testid={`match-exercise-${moduleId}`}>
      <div className="space-y-2 mb-4">
        {tools.map((tool) => (
          <div key={tool} className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-800 w-32 shrink-0">{tool}</span>
            <select
              value={selected[tool] || ''}
              onChange={(e) => setSelected({ ...selected, [tool]: e.target.value })}
              className="flex-1 px-3 py-2 rounded-lg border border-orange-200 text-sm bg-white outline-none"
              disabled={checked}
              data-testid={`match-select-${tool}`}
            >
              <option value="">{language === 'fr' ? 'Choisir...' : 'Choose...'}</option>
              {usages.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            {checked && (
              selected[tool] === pairs.find(p => p[0] === tool)?.[1]
                ? <Check className="w-4 h-4 text-emerald-500" />
                : <X className="w-4 h-4 text-red-400" />
            )}
          </div>
        ))}
      </div>
      {!checked ? (
        <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-600" onClick={handleCheck} disabled={Object.keys(selected).length < tools.length}>
          <Check className="w-3.5 h-3.5 mr-1" /> {language === 'fr' ? 'Vérifier' : 'Check'}
        </Button>
      ) : (
        <p className={`text-sm font-medium ${correct === tools.length ? 'text-emerald-600' : 'text-orange-600'}`}>
          {correct}/{tools.length} {language === 'fr' ? 'correct' : 'correct'}
          {correct === tools.length && (language === 'fr' ? ' - Parfait !' : ' - Perfect!')}
        </p>
      )}
    </div>
  );
}
